import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { PageShell, PageHero } from "@/components/site/page-shell";
import { supabase } from "@/integrations/supabase/client";
import { Star } from "lucide-react";

export const Route = createFileRoute("/testimonials")({
  component: TestimonialsPage,
  head: () => ({
    meta: [
      { title: "Testimonials — CoderLity" },
      { name: "description", content: "What founders and operators say about working with CoderLity." },
      { property: "og:title", content: "Testimonials — CoderLity" },
      { property: "og:description", content: "What founders and operators say about working with CoderLity." },
      { property: "og:url", content: "/testimonials" },
    ],
    links: [{ rel: "canonical", href: "https://coderlity.com/testimonials" }],
  }),
});

type Testimonial = {
  id: string; name: string; role: string | null; company: string | null;
  quote: string; rating: number; avatar_url: string | null;
};

function TestimonialsPage() {
  const { data, isLoading } = useQuery({
    queryKey: ["public", "testimonials"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("testimonials")
        .select("id,name,role,company,quote,rating,avatar_url")
        .eq("published", true)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as Testimonial[];
    },
  });

  return (
    <PageShell>
      <PageHero
        eyebrow="Loved by founders"
        title="What clients say about us"
        sub="Real words from teams we've shipped product, infra and growth for."
      />
      <section className="py-16">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          {isLoading && <p className="text-center text-muted-foreground">Loading…</p>}
          {!isLoading && !data?.length && (
            <p className="text-center text-muted-foreground">No testimonials yet.</p>
          )}
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {data?.map((t) => (
              <div key={t.id} className="rounded-2xl border border-border bg-card p-6 shadow-card">
                <div className="flex gap-0.5">
                  {Array.from({ length: t.rating }).map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-primary text-primary" />
                  ))}
                </div>
                <p className="mt-4 text-sm leading-relaxed">"{t.quote}"</p>
                <div className="mt-6 flex items-center gap-3">
                  {t.avatar_url ? (
                    <img src={t.avatar_url} alt={t.name} className="h-10 w-10 rounded-full object-cover" />
                  ) : (
                    <div className="h-10 w-10 rounded-full bg-gradient-brand" />
                  )}
                  <div>
                    <div className="text-sm font-semibold">{t.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {[t.role, t.company].filter(Boolean).join(", ")}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </PageShell>
  );
}
